import { useUserCache, useSubscriptionCache, useShowCache } from 'src/apollo/cache';
import { updateUserCache } from 'src/apollo/cache/update';
import { confirmSessionFromServer, confirmedUserVar } from 'src/apollo/sessionInvalidation';
import * as Sentry from '@sentry/react';
import posthog, { CapturedNetworkRequest, CaptureResult } from 'posthog-js';
import { useEffect } from 'react';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import {
  Permissions,
  ShowFragmentFragment,
  Subscription,
  UserFragmentFragment,
  useUpdateUserMutation,
} from 'src/generated/graphql';
import { captureClickIdsFromUrl } from 'src/utils/captureClickIdsFromUrl';
import { COOKIE_KEYS, getUtmCookies } from 'src/utils/getUtmCookies';
import { IToggle } from '@unleash/proxy-client-react';
import { isAdminImpersonating } from 'src/utils/impersonation';
import getGA4ClientId from 'src/utils/getGA4ClientId';
import { sha256Hash } from 'src/utils/sha256Hash';
import { usePermissionsContext } from '../permissions/PermissionsContext';
import { useGetAllFeatureFlags } from '../feature-flags/useFeatureFlag';

// Extend Window interface to include Bing UET tracking queue
declare global {
  interface Window {
    uetq: Array<any>;
  }
}

const isAdmin = (userRole?: string) => (userRole === undefined ? false : userRole === 'admin');

export default function useThirdPartyApps() {
  const user = useUserCache();
  const { permissions } = usePermissionsContext();
  const subscription = useSubscriptionCache();
  const show = useShowCache();
  const featureFlags = useGetAllFeatureFlags();
  const client = useApolloClient();
  const confirmedUser = useReactiveVar(confirmedUserVar);

  const [updateUser] = useUpdateUserMutation({
    onCompleted: res => {
      updateUserCache({ client, user: res.updateUser });
    },
  });

  useEffect(() => {
    captureClickIdsFromUrl();
  }, []);

  // A cached user may be restored from an ended session, so ask the server before the replay
  // identifies anyone. A revoked user is not asked about again: it stays revoked until a sign-in.
  const cachedUserId = user?.id;
  useEffect(() => {
    if (
      process.env.POSTHOG_BROWSER_TOKEN &&
      cachedUserId &&
      confirmedUserVar()?.id !== cachedUserId
    ) {
      confirmSessionFromServer(client);
    }
  }, [cachedUserId, client]);

  useEffect(() => {
    updateSessionReplay(confirmedUser, user);
  }, [confirmedUser, user]);

  useEffect(() => {
    if (shouldInitiate(user)) {
      const initializeApp = async () => {
        // Update user with GA4 client_id if available and not already stored
        const ga4ClientId = getGA4ClientId();
        if (ga4ClientId && user && !user.ga4ClientId) {
          await updateUser({
            variables: {
              ga4ClientId,
            },
          });
        }

        initSentry(user);
        initSegment(user, subscription, show, permissions, featureFlags);
        initBingTracking(user);
      };

      initializeApp();
    } else if (shouldInitiateSegment(user)) {
      initSentry(user);
      initSegment(user, subscription, show, permissions, featureFlags);
    }
  }, [user, subscription, show, permissions, featureFlags, updateUser, client]);
}

function shouldInitiate(user: UserFragmentFragment | undefined) {
  return process.env.NODE_ENV === 'production' && shouldInitiateSegment(user);
}

function shouldInitiateSegment(user: UserFragmentFragment | undefined) {
  // do not use third party apps if admin is using user's account
  return (
    (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') &&
    !(isAdminImpersonating() || isAdmin(user?.role))
  );
}

function formatFeatureFlags(flags: IToggle[]) {
  let result = {};

  const kebabToSnakeCase = (string: string) => string.replace(/-/g, '_');

  if (flags) {
    // Prepend the flag name with "ff_" (as in feature flag) and convert to snake case
    // to match other properties sent to Segment
    result = Object.fromEntries(
      flags?.map(flag => [`ff_${kebabToSnakeCase(flag.name)}`, flag.enabled]),
    );
  }
  return result;
}

function getISODateAtMidnight(date: number) {
  const toMidnight = new Date(date).setUTCHours(0, 0, 0, 0);
  return new Date(toMidnight).toISOString();
}

async function initBingTracking(user: UserFragmentFragment | undefined) {
  if (!user?.email) return;

  const hashedEmail = await sha256Hash(user.email);

  if (window.uetq.length === 0 || window.uetq[0]?.pid?.em !== hashedEmail) {
    window.uetq.push('set', {
      pid: {
        em: hashedEmail,
      },
    });
  }
}

function initSegment(
  user: UserFragmentFragment | undefined,
  subscription: Subscription | undefined,
  show: ShowFragmentFragment | undefined,
  permissions: Permissions | null | undefined,
  featureFlags: IToggle[],
) {
  try {
    // flags have to be in object to then spread them,
    // Segment does not work well with arrays or other complex entities.
    // We only want feature flag name and boolean value if it's enabled. e.g: flag: true
    const formattedFeatureFlags = formatFeatureFlags(featureFlags);

    if (!user || !show) return;
    const utmCookies = getUtmCookies();
    // @ts-ignore
    window.analytics?.identify(user.id, {
      user_id: user.id,
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      tier: permissions?.tier,
      trial_start_date: user?.createdAt && getISODateAtMidnight(+user.createdAt), // Posibly can be removed, needs discussion with Growth/Support team
      current_period_end:
        subscription?.current_period_end &&
        getISODateAtMidnight(subscription.current_period_end * 1000),
      subscription_plan: subscription?.plans?.[0]?.interval
        ? `${subscription.plans[0].interval}ly`
        : undefined,
      subscription_status: subscription?.status,
      coupon_used: subscription?.discount?.coupon?.id,
      host_is_connected: !!show.host,
      host_connected: show.host,
      email_opt_in: user.emailOptIn,
      is_tester: user.isTester,
      signed_up_at: user?.createdAt && getISODateAtMidnight(+user.createdAt),
      podcast_website: show.subdomain
        ? `${show.subdomain}${process.env.NODE_ENV === 'staging' ? '.staging' : ''}.alitu.com`
        : null,
      is_paused: !!subscription?.pause_collection,
      pause_resumes_at:
        subscription?.pause_collection?.resumes_at &&
        getISODateAtMidnight(subscription.pause_collection.resumes_at * 1000),
      ga4_client_id: user?.ga4ClientId,
      ...formattedFeatureFlags,
      distribution: {
        apple: show.links?.apple,
        spotify: show.links?.spotify,
      },
      [COOKIE_KEYS.FIRST_TOUCH.SOURCE]: utmCookies?.firstTouch?.source,
      [COOKIE_KEYS.FIRST_TOUCH.MEDIUM]: utmCookies?.firstTouch?.medium,
      [COOKIE_KEYS.FIRST_TOUCH.CAMPAIGN]: utmCookies?.firstTouch?.campaign,
      [COOKIE_KEYS.FIRST_TOUCH.CONTENT]: utmCookies?.firstTouch?.content,
      [COOKIE_KEYS.LAST_TOUCH.SOURCE]: utmCookies?.lastTouch?.source,
      [COOKIE_KEYS.LAST_TOUCH.MEDIUM]: utmCookies?.lastTouch?.medium,
      [COOKIE_KEYS.LAST_TOUCH.CAMPAIGN]: utmCookies?.lastTouch?.campaign,
      [COOKIE_KEYS.LAST_TOUCH.CONTENT]: utmCookies?.lastTouch?.content,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    posthog.captureException(error);
  }
}

// Recorded text can carry a password-reset token, a Stripe client secret, the forgot-page
// email, signed S3 or CloudFront credentials, and email addresses. Tracker beacons repeat the
// page URL percent-encoded, so the delimiters also match %2F, %3F, %26 and %3D, and escaped
// bytes inside a value are consumed. Every query value is replaced, so unknown signed
// parameters stay out too; parameter names are kept for diagnosis.
// ponytail: a URL encoded twice (%252F) inside a value is caught only because its whole
// enclosing value is replaced; decode nested parameters if a narrower rule is ever needed.
const RESET_TOKEN =
  /((?:\/|%2F)password-reset(?:\/|%2F))(?:[^/?#&%\s"'<>]|%(?!2F|3F|23|26)[0-9A-F]{2})+/gi;
const QUERY_VALUE =
  /((?:[?&]|%3F|%26)(?:[^=&#?%\s"'<>]|%(?!3D|26|23|3F)[0-9A-F]{2})+(?:=|%3D))(?:[^&#%\s"'<>)]|%(?!26|23)[0-9A-F]{2}|%(?![0-9A-F]{2}))+/gi;
// Also matches a percent-encoded @ (%40), as in mailto: links. Lengths are bounded so a long
// unbroken token cannot make the scan quadratic.
const EMAIL = /[A-Z0-9._%+-]{1,64}(?:@|%40)[A-Z0-9.-]{1,253}\.[A-Z]{2,24}/gi;
// Every recorded text node and attribute passes through here; most contain none of these.
const MAY_HOLD_SECRET = /password-reset|[?&@]|%3F|%26|%40/i;

const redactSecrets = (text: string) =>
  MAY_HOLD_SECRET.test(text)
    ? text
        .replace(RESET_TOKEN, '$1redacted')
        .replace(QUERY_VALUE, '$1redacted')
        .replace(EMAIL, email => '*'.repeat(email.length))
    : text;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype
  );
}

function redactDeep<T>(value: T): T {
  if (typeof value === 'string') return redactSecrets(value) as T;
  if (Array.isArray(value)) return value.map(redactDeep) as T;
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, redactDeep(item)]),
    ) as T;
  }
  return value;
}

function isConsoleRecord(value: unknown) {
  return (
    isPlainObject(value) &&
    value.type === 6 &&
    isPlainObject(value.data) &&
    value.data.plugin === 'rrweb/console@1'
  );
}

// Replay snapshots are mostly compressed before this hook, so only console entries, which the
// SDK leaves as plain strings, are redacted there. DOM text and attributes are redacted by the
// recorder's mask hooks before compression.
function redactEvent(event: CaptureResult): CaptureResult {
  if (event.event === '$snapshot') {
    const snapshots: unknown = event.properties.$snapshot_data;
    if (Array.isArray(snapshots)) {
      event.properties.$snapshot_data = snapshots.map(item =>
        isConsoleRecord(item) ? redactDeep(item) : item,
      );
    }
    return event;
  }
  return {
    ...event,
    properties: redactDeep(event.properties),
    $set: redactDeep(event.$set),
    $set_once: redactDeep(event.$set_once),
  };
}

// The recorder passes network requests and page URLs through this function. Headers are
// recorded only so that fetch and XHR are wrapped, which is what gives each request its method
// and status; they and the bodies are dropped here, before anything is sent.
const redactNetworkRequest = (request: CapturedNetworkRequest): CapturedNetworkRequest => ({
  ...request,
  name: redactSecrets(request.name),
  requestHeaders: undefined,
  responseHeaders: undefined,
  requestBody: undefined,
  responseBody: undefined,
});

function initPostHog(token: string) {
  posthog.init(token, {
    api_host: 'https://us.i.posthog.com',
    // Load only the recorder built for this SDK version: the redaction above depends on it.
    strict_script_versioning: true,
    // Session replay only: no product analytics.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    rageclick: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    // Not advanced_disable_flags, which also turns off the remote config replay needs.
    advanced_disable_feature_flags: true,
    // PostHog Logs bypasses before_send, and the project setting can switch console capture
    // on; drop every log record so only the redacted replay console is sent.
    logs: { captureConsoleLogs: false, beforeSend: () => null },
    person_profiles: 'identified_only',
    persistence: 'localStorage',
    save_referrer: false,
    save_campaign_params: false,
    capture_performance: { network_timing: true, web_vitals: false },
    enable_recording_console_log: true,
    before_send: redactEvent,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskTextFn: (text, element) =>
        element?.closest('.ph-mask') ? text.replace(/\S/g, '*') : redactSecrets(text),
      // Inline data: URIs carry bundled images, never secrets, and can be very long.
      maskAttributeFn: (_name, value) => (value.startsWith('data:') ? value : redactSecrets(value)),
      // data-private marks passwords, API keys, hosting keys and guest names. File inputs are
      // blocked because maskAllInputs leaves their value (the file name) readable.
      blockSelector: '[data-private], input[type="file"]',
      captureCanvas: { recordCanvas: false },
      recordHeaders: true,
      recordBody: false,
      maskCapturedNetworkRequestFn: redactNetworkRequest,
    },
  });
}

// Records eligible visitors, signed in or not, in any build that carries a token. A session
// that turns admin or impersonating stops for the rest of the page; leaving either one reloads
// the page. Only a user the server confirmed on this page is identified.
function updateSessionReplay(
  confirmedUser: UserFragmentFragment | undefined,
  cachedUser: UserFragmentFragment | undefined,
) {
  const token = process.env.POSTHOG_BROWSER_TOKEN;
  if (!token) return;

  if (isAdminImpersonating() || isAdmin(cachedUser?.role) || isAdmin(confirmedUser?.role)) {
    if (posthog.__loaded && !posthog.config.disable_session_recording) {
      posthog.stopSessionRecording();
      posthog.reset();
    }
    return;
  }

  if (!posthog.__loaded) {
    initPostHog(token);
  } else if (posthog.config.disable_session_recording) {
    return;
  }

  // Until the server confirms a user, a page load keeps the recording session it restored. Logout
  // and session invalidation revoke the confirmed user and reset explicitly.
  if (!confirmedUser) return;
  if (posthog.get_distinct_id() !== confirmedUser.id) {
    if (posthog._isIdentified()) posthog.reset();
    posthog.identify(confirmedUser.id);
  }
}

function initSentry(user: UserFragmentFragment | undefined) {
  if (!Sentry.isInitialized()) {
    Sentry.init({
      dsn: process.env.SENTRY_DSN,
      environment: process.env.NODE_ENV,
      integrations: [Sentry.browserTracingIntegration()],
      // Link each error to the replay recording at the time it happens.
      beforeSend: event =>
        posthog.sessionRecordingStarted()
          ? {
              ...event,
              extra: {
                ...event.extra,
                sessionURL: posthog.get_session_replay_url({ withTimestamp: true }),
              },
            }
          : event,
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: process.env.NODE_ENV === 'staging' ? 1.0 : 0.2,
      replaysOnErrorSampleRate: process.env.NODE_ENV === 'staging' ? 1.0 : 0.2,
      ignoreErrors: [
        'Invariant Violation: Cannot call hover while not dragging.',
        'AbortError: The play() request was interrupted by a call to pause()',
        "Cannot read property 'getCurrentTime' of null",
        "null is not an object (evaluating 'this.backend.getCurrentTime')",
      ],
    });
  }
  if (user) {
    Sentry.setUser({ id: user.id });
  }
}
